/* eslint-disable jsx-a11y/no-autofocus */
import { useState, useEffect, useRef, useCallback } from 'react';
import { SearchField } from '@backstage/ui';
import { useSearch } from '@backstage/plugin-search-react';

const DEBOUNCE_MS = 250;

export const SearchInput = () => {
  const { term, setTerm } = useSearch();
  const [localTerm, setLocalTerm] = useState(term);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isTypingRef = useRef(false);

  const searchFieldStyle = { minWidth: '256px' };

  // Keep the input in sync when the search term changes externally
  // (e.g. URL navigation or another component), but skip while the
  // user is actively typing to avoid overwriting their input.
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalTerm(term);
    }
  }, [term]);

  useEffect(() => {
    if (localTerm === term) return undefined;
    timerRef.current = setTimeout(() => {
      setTerm(localTerm);
      isTypingRef.current = false;
    }, DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [localTerm, term, setTerm]);

  const handleChange = useCallback((value: string) => {
    isTypingRef.current = true;
    setLocalTerm(value);
  }, []);

  const handleClear = useCallback(() => {
    clearTimeout(timerRef.current);
    isTypingRef.current = false;
    setLocalTerm('');
    setTerm('');
  }, [setTerm]);

  return (
    <SearchField
      autoFocus
      size="small"
      placeholder="Search..."
      value={localTerm}
      onChange={handleChange}
      onClear={handleClear}
      style={searchFieldStyle}
    />
  );
};
